import { FC, useEffect, useRef, useState } from 'react';
import { Button, Flex, Icon, Menu, MenuButton, MenuItem, MenuList, Text } from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import theme from '@adflow/theme';
import type { AnimationEffect } from '@adflow/types';
import { getPresetSelectionBlock, type AnimationPresetPhase } from '../../../animation';
import { ANIMATION_ROLE_LABEL, listPresetsForRole } from './presetDisplay';

type MenuStep = 'role' | AnimationPresetPhase;

type Props = {
  targetId: string;
  effects: readonly AnimationEffect[];
  onApply: (presetId: string) => void;
};

const roleItems: Array<{ phase: AnimationPresetPhase; testId: string }> = [
  { phase: 'IN', testId: 'animation-role-entrance' },
  { phase: 'OUT', testId: 'animation-role-exit' },
];

export const AddAnimationMenu: FC<Props> = ({ targetId, effects, onApply }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuStep, setMenuStep] = useState<MenuStep>('role');
  // Swapping the role list for presets blurs the focused item, and Chakra treats
  // that blur as an outside click. Ignore that one close so the second step stays open.
  const preserveOpen = useRef(false);
  const presets = menuStep === 'role' ? [] : listPresetsForRole(menuStep);

  useEffect(() => {
    // Child menu effects run first, so a blur-close from the step swap is ignored
    // before this clears the guard.
    preserveOpen.current = false;
  }, [menuStep]);

  const closeMenu = () => {
    if (preserveOpen.current) {
      preserveOpen.current = false;
      return;
    }
    setIsOpen(false);
    setMenuStep('role');
  };

  const showPresets = (phase: AnimationPresetPhase) => {
    preserveOpen.current = true;
    setMenuStep(phase);
  };

  return (
    <Menu
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={closeMenu}
      closeOnSelect={false}
      placement="bottom-start"
      strategy="fixed"
      computePositionOnMount
    >
      <MenuButton
        as={Button}
        variant="ghost"
        width="100%"
        bg="transparent"
        color={theme.palette.mcBlue.main}
        fontWeight="semibold"
        _hover={{ bg: 'gray.50' }}
        _active={{ bg: 'gray.50' }}
        data-test-id="add-animation-button"
      >
        + Add New Animation
      </MenuButton>
      <MenuList
        zIndex="dropdown"
        minW="220px"
        data-test-id={menuStep === 'role' ? 'animation-role-menu' : 'animation-preset-menu'}
        motionProps={{
          variants: {
            enter: {
              visibility: 'visible',
              opacity: 1,
              scale: 1,
              transition: { duration: 0.12 },
            },
            // Chakra's default exit sets visibility:hidden in transitionEnd, and that
            // write can land after the next open and leave the menu hidden.
            exit: {
              opacity: 0,
              scale: 0.98,
              transition: { duration: 0.08 },
            },
          },
        }}
      >
        {menuStep === 'role' ? (
          <>
            <Text px={3} pt={2} pb={1} fontSize="sm" color="gray.500">
              Select role
            </Text>
            {roleItems.map((role) => (
              <MenuItem
                key={role.phase}
                closeOnSelect={false}
                data-test-id={role.testId}
                onClick={() => showPresets(role.phase)}
              >
                {ANIMATION_ROLE_LABEL[role.phase]}
              </MenuItem>
            ))}
          </>
        ) : (
          presets.map((preset) => {
            const block = getPresetSelectionBlock(effects, targetId, preset.id);
            return (
              <MenuItem
                key={preset.id}
                isDisabled={block != null}
                data-test-id={`animation-preset-option-${preset.id}`}
                onClick={() => {
                  if (block != null) {
                    return;
                  }
                  onApply(preset.id);
                  setIsOpen(false);
                  setMenuStep('role');
                }}
              >
                <Flex width="100%" alignItems="center" justifyContent="space-between" gap={3}>
                  <Text>{preset.label}</Text>
                  {block === 'duplicate' ? <Icon as={LuCheck} boxSize={4} aria-hidden /> : null}
                </Flex>
              </MenuItem>
            );
          })
        )}
      </MenuList>
    </Menu>
  );
};
